/**
 * Tracks a set of job ids, not just one, since a user can create a second
 * request before the first finishes. Call trackJob(jobId) for each one.
 * Uses imperative RTK Query dispatches instead of a hook, since the number
 * of jobs to watch changes over time.
 *
 * The Document is created by a Celery task, not a client mutation, so
 * there's nothing to invalidate the cache directly. We watch each job's
 * status instead and invalidate once it succeeds.
 *
 * Polls right away when a job is tracked, not just on the next tick, so a
 * fast job doesn't sit around for up to 3 seconds before its result shows.
 * The isPollingRef guard stops that immediate call and the interval from
 * overlapping if one is still in flight. A ref, not a plain closure
 * variable, since pendingJobIds changing re-runs this effect and a plain
 * variable would reset to false on every re-render, briefly reopening the
 * overlap window if a new job gets tracked mid-poll.
 */
import { KaboodleToaster } from "components/common";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "services/api";
import { backgroundJobAPI } from "services/background-job-api";
import { useDispatch } from "utils/redux";

// Matches BackgroundJobSerializer's status field (get_status_display), the
// human-readable label, not the raw lowercase enum value. Same convention
// useMetadocAIFlow.ts uses for the same reason.
const TERMINAL_STATUSES = new Set(["Success", "Failed", "Error"]);
const POLL_INTERVAL_MS = 3000;

export const useDocumentGroupTagExtractionPolling = () => {
  const dispatch = useDispatch();
  const [pendingJobIds, setPendingJobIds] = useState<number[]>([]);
  const isPollingRef = useRef(false);

  const trackJob = useCallback((jobId: number) => {
    setPendingJobIds((current) =>
      current.includes(jobId) ? current : [...current, jobId],
    );
  }, []);

  useEffect(() => {
    if (pendingJobIds.length === 0) {
      return;
    }

    let cancelled = false;

    const pollOnce = async () => {
      if (isPollingRef.current) {
        return;
      }
      isPollingRef.current = true;

      try {
        const results = await Promise.all(
          pendingJobIds.map(async (jobId) => {
            try {
              const job = await dispatch(
                backgroundJobAPI.endpoints.getBackgroundJobById.initiate(
                  jobId,
                  { subscribe: false, forceRefetch: true },
                ),
              ).unwrap();
              return { jobId, status: job.status };
            } catch {
              // Counts as terminal so a deleted job isn't polled forever.
              // Downside: a transient network blip on a job that's still
              // actually running also shows the failure toast. Acceptable
              // for v1, no automatic retry here.
              return { jobId, status: "Error" as const };
            }
          }),
        );

        if (cancelled) {
          return;
        }

        const finishedJobIds = new Set<number>();
        for (const { jobId, status } of results) {
          if (TERMINAL_STATUSES.has(status)) {
            finishedJobIds.add(jobId);
            if (status === "Success") {
              // Invalidates the whole Documents tag rather than just this
              // file/client's queries, since there's no per-file tag to
              // target here. Fine at current scale, worth narrowing if
              // document queries ever get expensive.
              dispatch(api.util.invalidateTags(["Documents"]));
            } else {
              KaboodleToaster.error(
                "Failed to generate the extracted PDF. Please try again.",
              );
            }
          }
        }

        // Filters the live current state rather than replacing it with
        // this closure's own (possibly stale) pendingJobIds: a job tracked
        // after this poll started, while an earlier one from the same
        // batch was still in flight, must survive this update instead of
        // being silently dropped.
        if (!cancelled && finishedJobIds.size > 0) {
          setPendingJobIds((current) =>
            current.filter((id) => !finishedJobIds.has(id)),
          );
        }
      } finally {
        isPollingRef.current = false;
      }
    };

    void pollOnce();
    const pollTimer = setInterval(() => {
      void pollOnce();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(pollTimer);
    };
  }, [pendingJobIds, dispatch]);

  return { trackJob };
};
